import React, { useEffect, useState } from 'react'
import { invoke } from '@tauri-apps/api/core'
import { isHarmonyOS, isTauriMobile } from '../../utils/mobile'

// ★ 智能体访问（MCP）：桌面端 Tauri 命令返回结构
export interface McpStatus {
  enabled: boolean
  running: boolean
  port: number
  endpoint: string
  token: string
}
export interface McpAuditEntry {
  ts: number
  tool: string
  ok: boolean
  detail: string
}

// ★ 桌面 Tauri 环境判定（MCP 唯一宿主）：Tauri 壳且非移动端、非鸿蒙
// （菜单项显隐与卡片内部渲染共用同一判定，避免两处条件漂移）
export const isDesktopTauriEnv = (): boolean => {
  if (typeof window === 'undefined') return false
  const isTauriEnv = '__TAURI_INTERNALS__' in window || '__TAURI__' in window
  return isTauriEnv && !isTauriMobile() && !isHarmonyOS()
}

// ★ Trae MCP 配置模板（Streamable HTTP 型）：url/headers 填真实接入地址与令牌，
// 智能体 MCP 设置中直接粘贴。其他 HTTP 族客户端（Cursor/VS Code 等）骨架相同，type 字段略有差异
export const buildTraeConfigTemplate = (status: McpStatus): string =>
  JSON.stringify(
    {
      mcpServers: {
        lingshinote: {
          type: 'streamableHttp',
          url: status.endpoint,
          headers: {
            Authorization: `Bearer ${status.token}`,
          },
        },
      },
    },
    null,
    2,
  )

// ★ 豆包"自定义连接器"服务器 URL 模板：无法自定义请求头，令牌经 ?token= 查询参数传递
// （服务端双通道鉴权支持，见 src-tauri/src/mcp.rs mcp_endpoint）
export const buildDoubaoServerUrl = (status: McpStatus): string =>
  `${status.endpoint}?token=${status.token}`

/**
 * 智能体访问（MCP，P1 桌面只读）设置卡片：开关 + 接入地址 + 访问令牌 + 访问日志。
 * 自包含环境检测与 Tauri invoke 逻辑；非桌面 Tauri 环境渲染 null。
 * 使用处：SettingsPage（/settings 路由）、MobileUserMenu 桌面端弹层（Mac/Win 实际入口）。
 */
const McpSettingsCard: React.FC = () => {
  const [visible, setVisible] = useState(false)
  const [mcpStatus, setMcpStatus] = useState<McpStatus | null>(null)
  const [mcpBusy, setMcpBusy] = useState(false)
  const [mcpError, setMcpError] = useState('')
  const [showMcpToken, setShowMcpToken] = useState(false)
  const [mcpAudit, setMcpAudit] = useState<McpAuditEntry[] | null>(null)
  const [templateCopied, setTemplateCopied] = useState(false)
  const [doubaoCopied, setDoubaoCopied] = useState(false)

  useEffect(() => {
    if (isDesktopTauriEnv()) {
      setVisible(true)
      invoke<McpStatus>('mcp_get_status')
        .then(setMcpStatus)
        .catch((e) => console.warn('[MCP] 获取状态失败:', e))
    }
  }, [])

  const handleToggleMcp = async () => {
    if (mcpBusy) return
    setMcpBusy(true)
    setMcpError('')
    try {
      const next = !(mcpStatus?.enabled ?? false)
      const status = await invoke<McpStatus>('mcp_set_enabled', { enabled: next })
      setMcpStatus(status)
      if (!status.enabled) {
        setMcpAudit(null)
        setShowMcpToken(false)
      }
    } catch (e) {
      setMcpError(e instanceof Error ? e.message : String(e))
    } finally {
      setMcpBusy(false)
    }
  }

  const handleRotateMcpToken = async () => {
    if (mcpBusy) return
    setMcpBusy(true)
    setMcpError('')
    try {
      const status = await invoke<McpStatus>('mcp_rotate_token')
      setMcpStatus(status)
    } catch (e) {
      setMcpError(e instanceof Error ? e.message : String(e))
    } finally {
      setMcpBusy(false)
    }
  }

  const handleLoadMcpAudit = async () => {
    if (mcpAudit) {
      setMcpAudit(null)
      return
    }
    try {
      const entries = await invoke<McpAuditEntry[]>('mcp_get_audit_log', { limit: 50 })
      setMcpAudit(entries)
    } catch (e) {
      setMcpError(e instanceof Error ? e.message : String(e))
    }
  }

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
    } catch {
      /* 剪贴板不可用时静默忽略 */
    }
  }

  // ★ 一键复制 Trae 配置模板（含真实接入地址与令牌），2s 内显示"已复制"反馈
  const handleCopyTraeTemplate = async () => {
    if (!mcpStatus || templateCopied) return
    await copyToClipboard(buildTraeConfigTemplate(mcpStatus))
    setTemplateCopied(true)
    window.setTimeout(() => setTemplateCopied(false), 2000)
  }

  // ★ 一键复制豆包"自定义连接器"服务器 URL（?token= 令牌内嵌），2s 内显示"已复制"反馈
  const handleCopyDoubaoUrl = async () => {
    if (!mcpStatus || doubaoCopied) return
    await copyToClipboard(buildDoubaoServerUrl(mcpStatus))
    setDoubaoCopied(true)
    window.setTimeout(() => setDoubaoCopied(false), 2000)
  }

  if (!visible) return null

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
      <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-2">
        智能体访问（MCP）
      </h2>
      <p className="text-xs text-gray-400 dark:text-gray-500 mb-4">
        开启后，Trae / WorkBuddy 等支持 MCP 的智能体可通过本机接口读取你的笔记（只读，数据不出本机）
      </p>
      <button
        onClick={handleToggleMcp}
        disabled={mcpBusy}
        className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
      >
        <div className="flex items-center">
          <svg className="w-5 h-5 text-gray-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
          </svg>
          <div className="text-left">
            <div className="text-gray-700 dark:text-gray-300">
              允许智能体读取笔记{mcpBusy ? '（处理中…）' : ''}
            </div>
            <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
              {mcpError
                ? mcpError
                : mcpStatus?.enabled
                  ? mcpStatus.running
                    ? '运行中：智能体可按下方地址接入'
                    : '已开启但服务器未运行，请尝试重新开关'
                  : '关闭中：智能体无法访问任何数据'}
            </div>
          </div>
        </div>
        {/* ★ 开关：圆点用显式 left/top 定位（不依赖 translate + 静态位置推断，
            避免部分 WebView/flex 收缩场景下圆点漂出胶囊）；滑轨 shrink-0 防 flex 压缩 */}
        <span
          className={`relative inline-block shrink-0 w-10 h-6 rounded-full transition-colors ${
            mcpStatus?.enabled ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
          }`}
        >
          <span
            className={`absolute top-[2px] w-5 h-5 bg-white rounded-full shadow transition-all ${
              mcpStatus?.enabled ? 'left-[18px]' : 'left-[2px]'
            }`}
          />
        </span>
      </button>
      {mcpStatus?.enabled && (
        <div className="mt-3 space-y-2 text-sm">
          <div className="flex items-center justify-between gap-2">
            <span className="text-gray-500 dark:text-gray-400 shrink-0">接入地址</span>
            <button
              onClick={() => copyToClipboard(mcpStatus.endpoint)}
              className="flex-1 min-w-0 text-right font-mono text-xs text-blue-600 dark:text-blue-400 truncate hover:underline"
              title={mcpStatus.endpoint}
            >
              {mcpStatus.endpoint}（点击复制）
            </button>
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-gray-500 dark:text-gray-400 shrink-0">访问令牌</span>
            <div className="flex items-center gap-2 min-w-0">
              <button
                onClick={() => setShowMcpToken(!showMcpToken)}
                className="font-mono text-xs text-gray-700 dark:text-gray-300 truncate hover:underline"
                title="点击显示/隐藏"
              >
                {showMcpToken ? mcpStatus.token : '••••••••••••'}
              </button>
              {showMcpToken && (
                <button
                  onClick={() => copyToClipboard(mcpStatus.token)}
                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline shrink-0"
                >
                  复制
                </button>
              )}
              <button
                onClick={handleRotateMcpToken}
                disabled={mcpBusy}
                className="text-xs text-red-500 hover:underline shrink-0 disabled:opacity-50"
              >
                重新生成
              </button>
            </div>
          </div>
          <div className="flex items-start justify-between gap-2">
            <span className="text-gray-500 dark:text-gray-400 leading-snug">配置模板（适用：trae、workbuddy）</span>
            <button
              onClick={handleCopyTraeTemplate}
              className="text-xs text-blue-600 dark:text-blue-400 hover:underline shrink-0"
              title="复制含真实接入地址与令牌的 MCP 配置 JSON（trae / workbuddy 粘贴模板）"
            >
              {templateCopied ? '已复制 ✓' : '复制模板'}
            </button>
          </div>
          <div className="flex items-start justify-between gap-2">
            <span className="text-gray-500 dark:text-gray-400 leading-snug">豆包工作（自定义连接器 http 类型的服务器 URL）</span>
            <button
              onClick={handleCopyDoubaoUrl}
              className="text-xs text-blue-600 dark:text-blue-400 hover:underline shrink-0"
              title="复制豆包工作自定义连接器服务器 URL（令牌经 ?token= 内嵌，直接粘贴即可）"
            >
              {doubaoCopied ? '已复制 ✓' : '复制 URL'}
            </button>
          </div>
          <p className="text-xs text-gray-400 dark:text-gray-500">
            在智能体的 MCP 设置中添加服务器：URL 填接入地址，HTTP 请求头添加
            <code className="mx-1 px-1 py-0.5 bg-gray-100 dark:bg-gray-700 rounded font-mono text-[11px]">
              Authorization: Bearer &lt;访问令牌&gt;
            </code>
            。重新生成令牌会立即断开已接入的智能体。
          </p>
          {/* ★ 使用说明：各智能体添加方法 + 自然语言使用方法 */}
          <div className="rounded-lg border border-blue-100 dark:border-blue-900/40 bg-blue-50 dark:bg-blue-900/20 p-2.5 space-y-1.5">
            <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">
              <span className="font-medium">1、添加方法：</span>
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">
              · 豆包（新建工作任务）：左侧边栏"插件·技能·伙伴"→ 管理 → 添加 → 新建自定义连接器 → 输入名称，http 类型，粘贴上方"豆包工作"复制的服务器 URL
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">
              · trae cn（agent 模式）：设置 → MCP → 添加 → 手动配置 → 粘贴"配置模板"
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">
              · workbuddy：左侧边栏"专家·技能·连接器"→ 连接器 → 自定义连接器 → 添加 MCP → 粘贴"配置模板" → 信任连接器
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">
              <span className="font-medium">2、使用方法：</span>
              接入后即可在智能体软件中用自然语言直接操作，例如
              <span className="mx-0.5 text-gray-800 dark:text-gray-100">"帮我提取灵拾笔记中某某笔记的重点"</span>
              、"搜索灵拾笔记里关于预算的内容"。也可在左侧边栏笔记上右键"复制到智能体"，将
              <span className="mx-0.5 text-gray-800 dark:text-gray-100">"灵拾笔记/笔记名"</span>
              粘贴到对话中直接指代该笔记。如豆包中无法使用，请告诉豆包：
              <span className="mx-0.5 text-gray-800 dark:text-gray-100">"我添加了 lingshinote 连接器，帮我查询 lingshinote 中的内容"</span>
            </p>
          </div>
          <button
            onClick={handleLoadMcpAudit}
            className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
          >
            {mcpAudit ? '收起访问日志' : '查看访问日志'}
          </button>
          {mcpAudit && (
            <div className="max-h-56 overflow-y-auto text-xs border border-gray-100 dark:border-gray-700 rounded-lg p-2">
              {mcpAudit.length === 0 ? (
                <div className="text-gray-400 py-2 text-center">暂无访问记录</div>
              ) : (
                mcpAudit.map((entry, i) => (
                  <div
                    key={i}
                    className="py-1 border-b border-gray-100 dark:border-gray-700 last:border-0"
                  >
                    <span className="text-gray-400 mr-2">
                      {new Date(entry.ts * 1000).toLocaleString()}
                    </span>
                    <span className={entry.ok ? 'text-green-600 mr-2' : 'text-red-500 mr-2'}>
                      {entry.tool}
                    </span>
                    <span className="text-gray-500 dark:text-gray-400 break-all">
                      {entry.detail}
                    </span>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default McpSettingsCard
